import {
  getToken,
  removeToken,
  setToken,
} from "@/api/token";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface AuthUser {
  adminId: string;
  name: string;
  email: string;
  isMasterAccount: boolean;
  tokenExpiresAt: number;
}

export interface LoginData {
  adminId: string;
  name: string;
  email: string;
  isMasterAccount: boolean;
  accessToken: string;
  expiresIn: number;
}

interface AuthState {
  user: AuthUser | null;

  login: (data: LoginData) => void;
  updateUserName: (name: string) => void;
  logout: () => void;

  isTokenExpired: () => boolean;
  isAuthenticated: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,

      login: (data) => {
        // Save access token separately
        setToken(data.accessToken);

        // expiresIn is in seconds
        const tokenExpiresAt =
          Date.now() + data.expiresIn * 1000;

        // Save user information
        set({
          user: {
            adminId: data.adminId,
            name: data.name,
            email: data.email,
            isMasterAccount: data.isMasterAccount,
            tokenExpiresAt,
          },
        });
      },

      updateUserName: (name) => {
        set((state) =>
          state.user
            ? {
                user: {
                  ...state.user,
                  name,
                },
              }
            : state,
        );
      },

      logout: () => {
        removeToken();

        set({
          user: null,
        });
      },

      isTokenExpired: () => {
        const user = get().user;

        if (!user) {
          return true;
        }

        return user.tokenExpiresAt <= Date.now();
      },

      isAuthenticated: () => {
        const token = getToken();

        if (!token) {
          return false;
        }

        if (get().isTokenExpired()) {
          // Use the same logout logic
          get().logout();

          return false;
        }

        return true;
      },
    }),
    {
      name: "auth-user",
    },
  ),
);